import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get } from "@/api/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConciergeTemplate = {
  id: string;
  label: string;
};

export type PreviewLocation = {
  location_id: number;
  location_name: string;
  variables: Record<string, string>;
};

export type PreviewRecipient = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
};

export type PreviewResponse = {
  template: string;
  organization: { id: number; name: string };
  locations: PreviewLocation[];
  included: PreviewRecipient[];
  excluded: PreviewRecipient[];
};

export type SendResult = {
  sent: number;
  failed: number;
  recipients: { email: string; location: string; status: string }[];
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const emailConcierge = createQueryKeys("emailConcierge", {
  templates: () => ({
    queryKey: ["templates"],
    queryFn: async () => {
      return await get<ConciergeTemplate[]>(
        "notifications/email-concierge/templates/"
      );
    },
  }),
  preview: (params: {
    template: string;
    targetType: string;
    targetId: number | null;
    dashboardUrl?: string;
  }) => ({
    queryKey: ["preview", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        template: params.template,
        target_type: params.targetType,
        target_id: String(params.targetId),
      });
      if (params.dashboardUrl) {
        searchParams.set("dashboard_url", params.dashboardUrl);
      }
      return await get<PreviewResponse>(
        `notifications/email-concierge/preview/?${searchParams.toString()}`
      );
    },
  }),
});

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useEmailTemplates() {
  return useQuery(emailConcierge.templates());
}

export function useEmailConciergePreview(params: {
  template: string;
  targetType: string;
  targetId: number | null;
  dashboardUrl?: string;
}) {
  return useQuery({
    ...emailConcierge.preview(params),
    enabled:
      !!params.template && !!params.targetType && params.targetId !== null,
  });
}

export function useSendConciergeEmails() {
  return useMutation({
    mutationFn: async (data: {
      template: string;
      target_type: string;
      target_id: number;
      recipient_emails: string[];
      dashboard_url?: string;
      variable_overrides?: Record<string, string>;
    }) => {
      const res = await apiClient.post<SendResult>(
        "notifications/email-concierge/send/",
        data
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(
        `Sent ${data.sent} email${data.sent !== 1 ? "s" : ""} successfully${data.failed > 0 ? ` (${data.failed} failed)` : ""}`
      );
    },
    onError: () => {
      toast.error("Failed to send emails");
    },
  });
}
